import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import { 
  Search, MapPin, CheckCircle, Building2, Filter, Grid, List
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function BusinessDirectory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
        if (selectedState) params.append('state', selectedState);
        if (verifiedOnly) params.append('verified_only', 'true');
        if (searchQuery) params.append('q', searchQuery);

        const res = await axios.get(`${API}/api/business/directory?${params.toString()}`);
        setBusinesses(res.data.businesses);
        setCategories(res.data.categories);
        setTotalCount(res.data.total);
      } catch (error) {
        console.error('Error fetching businesses:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBusinesses();
  }, [selectedCategory, selectedState, verifiedOnly, searchQuery]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedState) params.append('state', selectedState);
      if (verifiedOnly) params.append('verified_only', 'true');
      if (searchQuery) params.append('q', searchQuery);

      const res = await axios.get(`${API}/api/business/directory?${params.toString()}`);
      setBusinesses(res.data.businesses);
      setCategories(res.data.categories);
      setTotalCount(res.data.total);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBusinesses();
  };

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.icon || '🏢';
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sidebar />
            <div>
              <h1 className="text-xl font-bold">Business Directory</h1>
              <p className="text-sm text-slate-500">{totalCount} local businesses</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search & Filters */}
        <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal mb-6">
          <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search businesses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="State (e.g., TX)"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value.toUpperCase())}
              className="w-[100px]"
              maxLength={2}
            />

            <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </form>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
                className="rounded border-slate-300"
              />
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Verified only
            </label>

            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-slate-200' : 'hover:bg-slate-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-slate-200' : 'hover:bg-slate-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          </div>
        )}

        {/* Results */}
        {!loading && (
          <>
            {businesses.length === 0 ? (
              <div className="bg-white border-2 border-black rounded-xl p-12 text-center shadow-brutal">
                <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h2 className="text-xl font-bold mb-2">No businesses found</h2>
                <p className="text-slate-500">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
                : 'space-y-4'
              }>
                {businesses.map((business) => (
                  <Link
                    key={business.user_id}
                    to={`/business/${business.user_id}`}
                    className="block bg-white border-2 border-black rounded-xl overflow-hidden shadow-brutal hover:shadow-brutal-lg transition-all hover:-translate-y-1"
                  >
                    {/* Banner/Header */}
                    <div className="h-24 bg-gradient-to-r from-indigo-500 to-purple-500 relative">
                      {business.profile_picture && (
                        <img 
                          src={business.profile_picture} 
                          alt={business.business_name}
                          className="absolute -bottom-8 left-4 w-16 h-16 rounded-xl border-4 border-white object-cover"
                        />
                      )}
                      {!business.profile_picture && (
                        <div className="absolute -bottom-8 left-4 w-16 h-16 rounded-xl border-4 border-white bg-slate-100 flex items-center justify-center text-2xl">
                          {getCategoryIcon(business.business_category)}
                        </div>
                      )}
                      {(business.verification_status === 'verified' || business.business_verified) && (
                        <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium text-emerald-600" data-testid={`verified-badge-${business.user_id}`}>
                          <CheckCircle className="w-3 h-3" /> Verified
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="pt-10 p-4">
                      <h3 className="font-bold text-lg mb-1">{business.business_name}</h3>
                      
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <span className="bg-slate-100 px-2 py-0.5 rounded-full">
                          {getCategoryIcon(business.business_category)} {getCategoryName(business.business_category)}
                        </span>
                      </div>

                      {(business.business_city || business.business_state) && (
                        <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                          <MapPin className="w-3 h-3" />
                          {business.business_city}{business.business_city && business.business_state && ', '}{business.business_state}
                        </div>
                      )}

                      {business.business_description && (
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {business.business_description}
                        </p>
                      )}

                      {business.business_visibility_tier !== 'community_host' && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            business.business_visibility_tier === 'market_sponsor' 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {business.business_visibility_tier === 'market_sponsor' ? '⭐ Market Sponsor' : '📍 Local Featured'}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
